import './MessageStrip.css';
import { CloseIcon, FaMessengerIcon, ImageIcon } from '@/components/ui/Icons';
import { AttachmentFileBadge } from '@/components/ui/AttachmentCard/AttachmentFileBadge';
import { ForwardedHeader } from '@/components/Messenger/shared/messages/ForwardedHeader';
import { isMessengerImageMime } from '@/components/Messenger/shared/attachments/messengerImageMime';
import { getMessengerContextAttachmentPreview } from '@/components/Messenger/shared/messages/messengerContextAttachmentPreview';
import type { ReactNode } from 'react';
import type { MessengerMessage } from '@/api/client';

function mediaKind(msg: MessengerMessage): 'image' | 'file' | null {
  if (msg.deleted_at) return null;
  if (!msg.file_url) return null;
  return isMessengerImageMime(msg.mime_type) ? 'image' : 'file';
}

/** Подпись строки вложения: картинка — всегда «Изображение», файл — имя или «Файл». */
function attachmentRowLabel(msg: MessengerMessage): string | null {
  const k = mediaKind(msg);
  if (!k) return getMessengerContextAttachmentPreview(msg.context_attachments)?.title ?? null;
  return k === 'image' ? 'Изображение' : msg.file_name?.trim() || 'Файл';
}

/** Превью картинки слева, сразу после иконки ответа / пересылки / редактирования. */
function MessageStripImageThumb({ msg }: { msg: MessengerMessage | null }): ReactNode {
  if (!msg || msg.deleted_at) return null;
  if (mediaKind(msg) !== 'image' || !msg.file_url) return null;
  return (
    <img src={msg.file_url} alt="" className="messageStrip__thumb" draggable={false} aria-hidden />
  );
}

function stackForMessage(msg: MessengerMessage): ReactNode {
  const isDeleted = Boolean(msg.deleted_at);
  const showImage = !isDeleted && Boolean(msg.file_url && isMessengerImageMime(msg.mime_type));
  const showFile = !isDeleted && Boolean(msg.file_url && !isMessengerImageMime(msg.mime_type));
  const contextPreview = !isDeleted ? getMessengerContextAttachmentPreview(msg.context_attachments) : null;
  const showContextAttachment = Boolean(contextPreview);
  const attachLabel = attachmentRowLabel(msg);

  const bodyOneLine = (msg.body?.trim() ?? '').replace(/\r?\n/g, ' ');
  const hasBody = Boolean(bodyOneLine);

  /** Картинка + текст: строку «Изображение» не дублируем — достаточно превью текста и миниатюры слева. */
  const showAttachmentRow =
    attachLabel != null && (showFile || showContextAttachment || (showImage && !hasBody));

  const fileRow = showAttachmentRow ? (
    <div className="messageStrip__fileRow">
      {showImage ? (
        <ImageIcon size={12} className="messageStrip__fileRowIcon" />
      ) : (
        <AttachmentFileBadge
          fileName={msg.file_name || attachLabel || 'file'}
          kind={contextPreview?.kind ?? 'file'}
          size="sm"
        />
      )}
      <span className="messageStrip__fileRowText">{attachLabel}</span>
    </div>
  ) : null;

  const forwardedSlot =
    !isDeleted && msg.forwarded_from_sender_name ? (
      <ForwardedHeader senderName={msg.forwarded_from_sender_name} isOwn={false} />
    ) : null;

  const bodyPreview =
    !isDeleted && bodyOneLine ? (
      <div className="messageStrip__bodyPreview">{bodyOneLine}</div>
    ) : null;

  const deletedPreview = isDeleted ? (
    <em className="messageStrip__deleted">Сообщение удалено</em>
  ) : null;

  const emptyFallback =
    !isDeleted && !fileRow && !forwardedSlot && !bodyPreview ? (
      <span className="messageStrip__empty">
        <em>Пустое сообщение</em>
      </span>
    ) : null;

  return (
    <>
      {forwardedSlot}
      {fileRow}
      {bodyPreview}
      {deletedPreview}
      {emptyFallback}
    </>
  );
}

/** «1 сообщение», «2 сообщения», «5 сообщений», «11 сообщений», «21 сообщение» … */
function forwardedMessagesCountPhrase(n: number): string {
  if (n <= 0) return '0 сообщений';
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} сообщение`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} сообщения`;
  return `${n} сообщений`;
}

export type MessageStripProps =
  | {
      variant: 'reply' | 'edit';
      message: MessengerMessage;
      /** Имя в заголовке: «Вы» или собеседник */
      senderLabel: string;
      onCancel: () => void;
    }
  | {
      variant: 'forward';
      messages: MessengerMessage[];
      getSenderName: (msg: MessengerMessage) => string;
      onCancel: () => void;
    };

export function MessageStrip(props: MessageStripProps): ReactNode {
  const { onCancel } = props;

  if (props.variant === 'forward') {
    const { messages, getSenderName } = props;
    if (messages.length === 0) return null;

    const first = messages[0]!;
    const authorName = getSenderName(first);
    const title = `Переслано от ${authorName}`;
    const multi = messages.length > 1;

    return (
      <div className="messageStrip">
        <div className="messageStrip__glyph" aria-hidden>
          <FaMessengerIcon icon="share" size={24} className="messageStrip__glyphIcon" />
        </div>
        {multi ? null : <MessageStripImageThumb msg={first} />}
        <div className="messageStrip__pill">
          <div className="messageStrip__bar" aria-hidden />
          <div className="messageStrip__stack">
            <span className="messageStrip__title">{title}</span>
            {multi ? (
              <span className="messageStrip__subTitle">{forwardedMessagesCountPhrase(messages.length)}</span>
            ) : null}
            {multi ? null : stackForMessage(first)}
          </div>
        </div>
        <button
          type="button"
          className="chatAssistant__close"
          onClick={onCancel}
          aria-label="Отменить пересылку"
        >
          <CloseIcon size={20} />
        </button>
      </div>
    );
  }

  const { variant, message, senderLabel } = props;
  const isEdit = variant === 'edit';

  return (
    <div className="messageStrip">
      <div className="messageStrip__glyph" aria-hidden>
        {isEdit ? (
          <FaMessengerIcon icon="pencil" size={24} className="messageStrip__glyphIcon" />
        ) : (
          <FaMessengerIcon icon="replyd" size={24} className="messageStrip__glyphIcon" />
        )}
      </div>
      <MessageStripImageThumb msg={message} />
      <div className="messageStrip__pill">
        <div className="messageStrip__bar" aria-hidden />
        <div className="messageStrip__stack">
          <span className="messageStrip__title">{senderLabel}</span>
          {stackForMessage(message)}
        </div>
      </div>
      <button
        type="button"
        className="chatAssistant__close"
        onClick={onCancel}
        aria-label={isEdit ? 'Отменить редактирование' : 'Отменить ответ'}
      >
        <CloseIcon size={20} />
      </button>
    </div>
  );
}
